import axios from 'axios';
import { createOnboardingAppUrl } from '../Actions';

// Create an axios instance with default configuration
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  withCredentials: true, // This ensures cookies are sent with requests
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to properly handle token
axiosInstance.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging and handling 401 redirects
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      if (error.response.status === 401) {
        console.error('Authentication Error:', {
          status: error.response.status,
          message: error.response.data?.message || 'No error message provided',
          endpoint: error.config.url
        });
        
        // Check if the backend indicates we should redirect to onboarding
        if (error.response.data?.shouldRedirectToOnboarding) {
          console.log('Redirecting to onboarding due to authentication failure');
          window.location.href = createOnboardingAppUrl({ 
            path: '/welcome',
            appendSource: true
          });
          // Return a promise that never resolves to prevent further error handling
          return new Promise(() => {});
        }
      } else {
        console.error('Response Error:', {
          status: error.response.status,
          headers: error.response.headers,
          data: error.response.data,
          url: error.config.url
        });
      }
    } else if (error.request) {
      console.error('Request Error (No Response):', error.request);
      console.error('Request URL:', error.config.url);
    } else {
      console.error('Error:', error.message);
    }
    return Promise.reject(error);
  }
);

// Get request Function
export const apiGetRequest = (endpoint, props = {}) =>
  apiRequest('GET', endpoint, props);

// Post request Function
export const apiPostRequest = (endpoint, payload, headers) => {
  // Only include the headers if user provides them
  const params = { 
    data: payload, 
    ...(headers ? { headers } : {}) 
  };

  return apiRequest('POST', endpoint, params);
};

// Put Request Function
export const apiPutRequest = (endpoint, payload, headers) => {
  const params = {
    data: payload,
    ...(headers ? { headers } : {}),
  };
  return apiRequest('PUT', endpoint, params);
};

// Delete Request Function
export const apiDeleteRequest = (endpoint, payload) =>
  apiRequest('DELETE', endpoint, { data: payload });

// Api Request for all the api methods
export const apiRequest = (method, endpoint, props = {}) => {
  const params = {
    method,
    url: endpoint,
    ...props,
  };

  return axiosInstance(params);
};

export default axiosInstance;